/**
 * MILQ header — automatic contrast engine.
 * Samples the page behind the transparent header and toggles
 * `.milq-header--on-dark` (white text) when the backdrop is majority dark.
 * Loaded only when the "Automatic header contrast" theme setting is on.
 */
(function () {
  'use strict';

  var SAMPLE_XS = [0.08, 0.3, 0.5, 0.7, 0.92];
  var LUM_THRESHOLD = 0.7;
  var ON_DARK_CLASS = 'milq-header--on-dark';
  var BAIL_CLASSES = ['show-sub-nav', 'show-search', 'show-minicart'];

  // src -> number (average luminance) | null (unsampleable) | Promise (loading)
  // Capped FIFO: the context persists across Turbolinks navigations.
  var imageLuminanceCache = new Map();
  var CACHE_LIMIT = 100;

  function setCache(key, value) {
    if (imageLuminanceCache.size >= CACHE_LIMIT && !imageLuminanceCache.has(key)) {
      imageLuminanceCache.delete(imageLuminanceCache.keys().next().value);
    }
    imageLuminanceCache.set(key, value);
  }
  var lastPointResults = [];
  var ticking = false;
  var listenersBound = false;
  var observer = null;
  var bodyObserver = null;

  function getHeader() {
    return document.querySelector('[data-milq-header]');
  }

  function parseColor(str) {
    var m = /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/.exec(str);
    if (!m) return null;
    return {
      r: parseFloat(m[1]),
      g: parseFloat(m[2]),
      b: parseFloat(m[3]),
      a: m[4] === undefined ? 1 : parseFloat(m[4]),
    };
  }

  function luminance(r, g, b) {
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  function extractBgImageUrl(style) {
    var bg = style.backgroundImage;
    if (!bg || bg === 'none') return null;
    var m = /url\(["']?([^"')]+)["']?\)/.exec(bg);
    return m ? m[1] : null;
  }

  /**
   * Best-effort average luminance of the top strip of an image.
   * Resolves to a number, or null when the image can't be sampled (CORS etc).
   */
  function sampleImageLuminance(src) {
    if (imageLuminanceCache.has(src)) return imageLuminanceCache.get(src);

    var promise = new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var w = 16;
          var h = 16;
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          // Top rows only — the strip the header actually overlaps.
          var data = ctx.getImageData(0, 0, w, 5).data;
          var total = 0;
          var count = 0;
          for (var i = 0; i < data.length; i += 4) {
            if (data[i + 3] === 0) continue;
            total += luminance(data[i], data[i + 1], data[i + 2]);
            count++;
          }
          resolve(count ? total / count : null);
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = function () {
        resolve(null);
      };
      img.src = src;
    }).then(function (value) {
      setCache(src, value);
      // Re-evaluate once the real value is known.
      requestEvaluate();
      return value;
    });

    setCache(src, promise);
    return promise;
  }

  /**
   * Classifies one sample point as true (dark), false (light) or null (unknown yet).
   */
  function classifyPoint(x, y, header) {
    var stack;
    try {
      stack = document.elementsFromPoint(x, y);
    } catch (e) {
      return null;
    }
    if (!stack) return null;

    for (var i = 0; i < stack.length; i++) {
      var el = stack[i];
      if (!el) continue;
      if (header && (header === el || header.contains(el))) continue;
      if (el.classList && el.classList.contains('ribbon')) {
        // The ribbon is an overlay — the surface behind it decides.
        continue;
      }

      var style = window.getComputedStyle(el);
      if (!style) continue;

      var color = parseColor(style.backgroundColor);
      if (color && color.a > 0.5) {
        return luminance(color.r, color.g, color.b) < LUM_THRESHOLD;
      }

      var src = el.tagName === 'IMG' && el.currentSrc ? el.currentSrc : extractBgImageUrl(style);
      if (src) {
        var cached = imageLuminanceCache.get(src);
        if (cached === undefined) {
          sampleImageLuminance(src);
          return null; // unknown this pass; re-run fires when it resolves
        }
        if (cached === null || typeof cached.then === 'function') {
          if (cached === null) continue; // unsampleable — look further down the stack
          return null; // still loading
        }
        return cached < LUM_THRESHOLD;
      }
    }

    return false; // nothing opaque found — page background is light
  }

  function evaluate() {
    ticking = false;

    var header = getHeader();
    if (!header) return;

    for (var b = 0; b < BAIL_CLASSES.length; b++) {
      if (document.body.classList.contains(BAIL_CLASSES[b])) {
        header.classList.remove(ON_DARK_CLASS);
        return;
      }
    }
    if (document.documentElement.classList.contains('search-expanded')) {
      header.classList.remove(ON_DARK_CLASS);
      return;
    }

    var rect = header.getBoundingClientRect();
    var y = rect.top + rect.height / 2;
    if (y < 0) return;

    var dark = 0;
    var light = 0;

    for (var i = 0; i < SAMPLE_XS.length; i++) {
      var x = Math.round(window.innerWidth * SAMPLE_XS[i]);
      var result = classifyPoint(x, y, header);
      if (result === null) {
        // Unknown (image still loading) — reuse the previous result for stability.
        result = lastPointResults[i] !== undefined ? lastPointResults[i] : null;
      }
      lastPointResults[i] = result;
      if (result === true) dark++;
      else if (result === false) light++;
    }

    header.classList.toggle(ON_DARK_CLASS, dark > light);
  }

  function requestEvaluate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(evaluate);
  }

  function init() {
    lastPointResults = [];
    if (!getHeader()) return;
    requestEvaluate();

    // Body is swapped by Turbolinks — re-observe it fresh each load.
    if (bodyObserver) {
      bodyObserver.disconnect();
    } else {
      bodyObserver = new MutationObserver(requestEvaluate);
    }
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    if (listenersBound) return;
    listenersBound = true;

    window.addEventListener('scroll', requestEvaluate, { passive: true });
    window.addEventListener('resize', requestEvaluate, { passive: true });
    window.addEventListener('load', requestEvaluate);

    // Overlay classes (search / minicart / mobile nav) flip the bar's state too.
    observer = new MutationObserver(requestEvaluate);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('turbolinks:load', init);
})();
